"use client";

import Link from "next/link";
import { useEffect, useState } from "react";

import { ContactIconButtons } from "@/components/ownly/ContactActions";
import { BOOKING_CTA, OWNLY_BOOKING_HREF } from "@/lib/booking";

/**
 * A slim booking bar for phones on the long briefing page.
 *
 * Only below lg, where the header's own booking link is tucked into the menu. On a phone the hero's
 * booking button sits below the video, off the first screen, so the bar shows whenever no other
 * booking button is in view, and steps aside whenever another booking button or
 * the free-guide form is on screen, so there is never a second copy of something already visible
 * and it never sits over the guide form's fields. Watched elements are marked with
 * `data-booking-bar="hide"`.
 *
 * WhatsApp and call sit beside the booking button as two round buttons (Tom, 2026-10-11), so the
 * one bar that already owns the bottom of the screen offers every way in, instead of a second
 * floating bubble stacked on top of it.
 *
 * Safari rules for fixed chrome over moving content: a solid, near-opaque background (no
 * backdrop blur), and it slides with transform only. Reduced motion drops the slide.
 */
export function StickyBookingBar() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const targets = Array.from(document.querySelectorAll<HTMLElement>('[data-booking-bar="hide"]'));
    if (!targets.length || typeof IntersectionObserver === "undefined") return;
    const onScreen = new Set<Element>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) onScreen.add(entry.target);
          else onScreen.delete(entry.target);
        }
        setVisible(onScreen.size === 0);
      },
      { threshold: 0 },
    );
    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
  }, []);

  return (
    <div
      aria-hidden={!visible}
      className={`fixed inset-x-0 bottom-0 z-40 border-t border-white/[0.12] bg-[#070d18]/[0.97] px-4 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-3 shadow-[0_-18px_40px_-24px_rgba(0,0,0,0.9)] transition-transform duration-300 ease-out will-change-transform motion-reduce:transition-none lg:hidden ${
        visible ? "translate-y-0" : "pointer-events-none translate-y-full"
      }`}
    >
      <div className="flex items-center gap-2.5">
        <Link
          href={OWNLY_BOOKING_HREF}
          tabIndex={visible ? 0 : -1}
          data-cta="sticky-bar"
          className="ownly-control ownly-btn-primary flex h-12 min-w-0 flex-1 items-center justify-center rounded-full px-4 text-white shadow-[var(--ownly-cta-shadow)]"
        >
          <span className="truncate">{BOOKING_CTA}</span>
        </Link>
        <ContactIconButtons tabbable={visible} />
      </div>
    </div>
  );
}
